import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { AiUsageSummary, AiUsageTenantRow } from '@bliks-io/contracts';
import {
  DataTable,
  createColumnHeader,
  Page,
  PageContent,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { useSuperAdminAiUsage } from '@/pages/admin/api/queries/useSuperAdminAiUsage';
import { useListTenants } from '@/pages/admin/api/queries/useTenants';
import { formatMicroEur, formatNumber } from '@/lib/formatters';
import type { TFn } from '@/lib/i18n';
import { SummaryCard } from '@/pages/admin/components/SummaryCard';
import { AiUsageSourceTable } from './AiUsageSourceTable';
import { AiUsageWarnings } from './AiUsageWarnings';
import { totalCallsOf, totalTokensOf } from './aiUsageTotals';

/** Single authority on the range vocabulary — the route's search schema
 *  derives its enum from this list. */
export const AI_USAGE_RANGES = ['7d', '30d', '90d', 'all'] as const;
export type AiUsageRange = (typeof AI_USAGE_RANGES)[number];

const RANGE_DAYS: Record<Exclude<AiUsageRange, 'all'>, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90
};
const RANGE_LABEL_KEYS: Record<AiUsageRange, string> = {
  '7d': 'aiUsage.range7d',
  '30d': 'aiUsage.range30d',
  '90d': 'aiUsage.range90d',
  all: 'aiUsage.rangeAll'
};
const DEFAULT_RANGE: AiUsageRange = '30d';

/**
 * "Last N days" = today (partial) plus the N−1 previous full days, anchored
 * to local midnight so the query key (and thus the cache) stays stable
 * across re-renders within a day.
 */
function rangeDateFrom(range: AiUsageRange): string | undefined {
  if (range === 'all') return undefined;
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  from.setDate(from.getDate() - (RANGE_DAYS[range] - 1));
  return from.toISOString();
}

function formatMinutes(t: TFn, language: string, seconds: number): string {
  return t('aiUsage.minutesSuffix', {
    value: new Intl.NumberFormat(language, { maximumFractionDigits: 1 }).format(seconds / 60)
  });
}

function buildSummaryCards(t: TFn, language: string, summary: AiUsageSummary | undefined) {
  const totalCalls = totalCallsOf(summary);

  return [
    {
      label: t('aiUsage.totalCost'),
      value: formatMicroEur(summary?.totalCostMicroEur ?? 0, language),
      context: t('aiUsage.acrossCalls', { count: totalCalls })
    },
    {
      label: t('aiUsage.calls'),
      value: formatNumber(totalCalls, language),
      context: t('aiUsage.callsBreakdown', {
        generation: formatNumber(summary?.generationCount ?? 0, language),
        embedding: formatNumber(summary?.embeddingCount ?? 0, language),
        transcription: formatNumber(summary?.transcriptionCount ?? 0, language)
      })
    },
    {
      label: t('aiUsage.tokens'),
      value: formatNumber(totalTokensOf(summary), language),
      context: t('aiUsage.tokensBreakdown', {
        input: formatNumber(summary?.totalInputTokens ?? 0, language),
        output: formatNumber(summary?.totalOutputTokens ?? 0, language)
      })
    },
    {
      label: t('aiUsage.transcription'),
      value: formatMinutes(t, language, summary?.totalTranscriptionSeconds ?? 0),
      context: t('aiUsage.acrossCalls', { count: summary?.transcriptionCount ?? 0 })
    }
  ];
}

/** Drop the DataTable-namespaced URL state of both usage tables — switching
 *  scope unmounts them, and their stale sort/filter params would otherwise
 *  linger in the URL. */
function withoutTableState(search: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(search).filter(
      ([key]) => !key.startsWith('aiUsageTenants_') && !key.startsWith('aiUsageSources_')
    )
  );
}

export function AiUsagePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: '/admin/ai-usage/' });

  const range: AiUsageRange = search.range ?? DEFAULT_RANGE;
  const dateFrom = useMemo(() => rangeDateFrom(range), [range]);

  const { data: tenantsData } = useListTenants();
  const { data, isLoading, isError, isPlaceholderData } = useSuperAdminAiUsage({
    tenantId: search.tenantId,
    dateFrom
  });

  const summary = data?.summary;
  const tenants = data?.tenants ?? [];
  const sources = data?.sources ?? [];

  const setTenant = useCallback(
    (tenantId?: string) =>
      navigate({ to: '/admin/ai-usage', search: { ...withoutTableState(search), tenantId } }),
    [navigate, search]
  );

  const setRange = useCallback(
    (nextRange: AiUsageRange) =>
      navigate({ to: '/admin/ai-usage', search: { ...search, range: nextRange } }),
    [navigate, search]
  );

  const selectedTenantName = tenantsData?.tenants?.find(
    (tenant) => tenant._id === search.tenantId
  )?.name;

  const summaryCards = buildSummaryCards(t, i18n.language, summary);

  const tenantColumns = useMemo<DataTableColumnDef<AiUsageTenantRow>[]>(
    () => [
      {
        accessorKey: 'tenantName',
        header: createColumnHeader(t('aiUsage.tenant')),
        cell: ({ row }) => (
          <button
            className="text-left font-medium hover:underline"
            onClick={() => setTenant(row.original.tenantId)}
          >
            {row.original.tenantName}
          </button>
        )
      },
      {
        accessorKey: 'eventCount',
        header: createColumnHeader(t('aiUsage.calls')),
        cell: ({ row }) => formatNumber(row.original.eventCount, i18n.language)
      },
      {
        accessorKey: 'totalTokens',
        header: createColumnHeader(t('aiUsage.tokens')),
        cell: ({ row }) => formatNumber(row.original.totalTokens, i18n.language)
      },
      {
        accessorKey: 'transcriptionSeconds',
        header: createColumnHeader(t('aiUsage.transcription')),
        cell: ({ row }) => formatMinutes(t, i18n.language, row.original.transcriptionSeconds)
      },
      {
        accessorKey: 'totalCostMicroEur',
        header: createColumnHeader(t('aiUsage.cost')),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {formatMicroEur(row.original.totalCostMicroEur, i18n.language)}
          </span>
        )
      }
    ],
    [t, setTenant, i18n.language]
  );

  return (
    <Page>
      <PageContent>
        <div className="mb-6">
          <h1 className="text-2xl font-bold">{t('aiUsage.title')}</h1>
          <p className="text-muted-foreground">{t('aiUsage.description')}</p>
        </div>

        {/* Scope toolbar — tenant drill-down plus the createdAt window */}
        <div className="mb-4 flex flex-wrap gap-4">
          <div>
            <Label>{t('aiUsage.scope')}</Label>
            <Select value={search.tenantId ?? ''} onValueChange={(v) => setTenant(v || undefined)}>
              <SelectTrigger
                allowClear
                hasValue={!!search.tenantId}
                onClear={() => setTenant(undefined)}
              >
                <SelectValue placeholder={t('aiUsage.allTenants')} />
              </SelectTrigger>
              <SelectContent>
                {tenantsData?.tenants?.map((tenant) => (
                  <SelectItem key={tenant._id} value={tenant._id}>
                    {tenant.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('aiUsage.range')}</Label>
            <Select value={range} onValueChange={(v) => setRange(v as AiUsageRange)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AI_USAGE_RANGES.map((rangeOption) => (
                  <SelectItem key={rangeOption} value={rangeOption}>
                    {t(RANGE_LABEL_KEYS[rangeOption])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* On error, render only the message — zeroed cards would misread as
            "no usage" on a cost dashboard. */}
        {isError && <p className="mb-4 text-sm text-destructive">{t('aiUsage.loadError')}</p>}
        {!isError && (
          <>
            {/* Dim stale keepPreviousData numbers while the new scope loads. */}
            <div
              className={`mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 ${
                isPlaceholderData ? 'opacity-50' : ''
              }`}
            >
              {summaryCards.map((card) => (
                <SummaryCard key={card.label} {...card} isLoading={isLoading} />
              ))}
            </div>

            <div className="mb-4 space-y-2 empty:hidden">
              <AiUsageWarnings summary={summary} failedTenantCount={data?.failedTenantCount} />
            </div>

            {search.tenantId ? (
              <>
                {selectedTenantName && (
                  <h2 className="mb-3 text-lg font-semibold">{selectedTenantName}</h2>
                )}
                <AiUsageSourceTable sources={sources} isLoading={isLoading || isPlaceholderData} />
              </>
            ) : (
              <DataTable
                columns={tenantColumns}
                data={tenants}
                isLoading={isLoading || isPlaceholderData}
                enableSorting
                tableId="aiUsageTenants"
                emptyMessage={t('aiUsage.empty')}
              />
            )}
          </>
        )}
      </PageContent>
    </Page>
  );
}
